/**
 * Pure, framework-free consent helpers (SAM-194).
 *
 * Kept free of `$app/*`, `posthog`, and browser globals so the logic project
 * (Node env) can test it directly. The browser-bound store wires these to
 * localStorage / navigator / PostHog.
 */

/** localStorage key holding the user's analytics consent decision. */
export const CONSENT_STORAGE_KEY = 'cookie-consent';

/** The user's explicit choice. `null` means "not decided yet". */
export type ConsentDecision = 'accepted' | 'declined';

/** Narrow a raw stored value to a known decision, or null when unset/invalid. */
export function parseConsentValue(raw: string | null | undefined): ConsentDecision | null {
	return raw === 'accepted' || raw === 'declined' ? raw : null;
}

/**
 * Whether any Do Not Track signal is enabled. Browsers expose DNT through a few
 * different properties; values are usually strings but some expose a boolean.
 * Kept in lockstep with PostHog's own `respect_dnt` truthiness check so the
 * banner-hide logic and PostHog's capture-block never disagree.
 */
export function isDoNotTrackEnabled(
	...signals: Array<string | boolean | null | undefined>
): boolean {
	return signals.some(
		(signal) => signal === '1' || signal === 'yes' || signal === 'true' || signal === true
	);
}

/**
 * The banner appears only when the user has made no decision and has not asked
 * not to be tracked. A stored decision (either way) or DNT keeps it hidden.
 */
export function shouldShowConsentBanner(opts: {
	decision: ConsentDecision | null;
	doNotTrack: boolean;
}): boolean {
	return opts.decision === null && !opts.doNotTrack;
}

/**
 * Whether a stored decision should be replayed into PostHog as an opt-in on app
 * start. Do Not Track always wins: a previously-consenting user who later turns
 * DNT on must NOT be opted back in (otherwise `identifyUser()` becomes eligible
 * again and could re-establish identity despite DNT). See SAM-194.
 */
export function shouldOptInOnInit(opts: {
	decision: ConsentDecision | null;
	doNotTrack: boolean;
}): boolean {
	return opts.decision === 'accepted' && !opts.doNotTrack;
}

/**
 * Effective consent to persist server-side (SAM-752). Mirrors the SDK gating:
 * accepted without DNT → true; declined or DNT → false; no decision → null
 * (nothing to persist). DNT always wins, in lockstep with shouldOptInOnInit().
 */
export function effectiveConsent(opts: {
	decision: ConsentDecision | null;
	doNotTrack: boolean;
}): boolean | null {
	if (opts.decision === null) return null;
	return opts.decision === 'accepted' && !opts.doNotTrack;
}

/**
 * Replay a stored local decision to the backend when the backend has none yet
 * (users who answered the banner before analytics_consent existed). SAM-752.
 */
export function shouldReplayConsent(opts: {
	decision: ConsentDecision | null;
	backendConsent: boolean | null;
}): boolean {
	return opts.decision !== null && opts.backendConsent === null;
}
